import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { apiHooks } from '../../../../hooks/useRestAPIHooks';
import { useActiveProjectId } from '../../../../hooks/useActiveProjectId';
import { useService } from '../../../../hooks';
import { featuresSelectors } from '../../../../redux/modules/features';
import SurveyService from '../../../../services/SurveyService';
import {
  SurveyContext,
  SurveyResponseContext,
  SubjectIdContext,
  IsReadOnlyContext,
  AutoSaveContext,
} from '../Contexts';

export type SurveyFillTask = {
  id: string;
  status: string;
  created?: number;
  agentInput?: { response_id?: string };
  agentOutput?: {
    filledCount?: number;
    filled_count?: number;
    fhirFilledCount?: number;
    fhir_filled_count?: number;
    zoriFilledCount?: number;
    zori_filled_count?: number;
    failureReason?: string;
    failure_reason?: string;
  };
};

export type SurveyFillSourceCounts = {
  fhirFilledCount: number;
  zoriFilledCount: number;
};

const asNonNegativeCount = (value: unknown): number | undefined => {
  // DynamoDB Decimals historically leaked through FastAPI as JSON strings
  // ("2") while typed fields like created stayed numbers. Accept both.
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return undefined;
    }
    return Math.trunc(parsed);
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return undefined;
  }
  return Math.trunc(value);
};

/**
 * Structured FHIR fills vs Zori LLM fills from a completed survey-fill task.
 * Returns undefined when either count is missing so the UI can show a truthful
 * fallback rather than inventing zeros or source splits for older payloads.
 */
export const surveyFillSourceCounts = (
  agentOutput?: SurveyFillTask['agentOutput'],
): SurveyFillSourceCounts | undefined => {
  if (!agentOutput) {
    return undefined;
  }
  const fhirFilledCount =
    asNonNegativeCount(agentOutput.fhirFilledCount) ??
    asNonNegativeCount(agentOutput.fhir_filled_count);
  const zoriFilledCount =
    asNonNegativeCount(agentOutput.zoriFilledCount) ??
    asNonNegativeCount(agentOutput.zori_filled_count);
  if (fhirFilledCount === undefined || zoriFilledCount === undefined) {
    return undefined;
  }
  return { fhirFilledCount, zoriFilledCount };
};

/**
 * Total answers pre-filled by a completed survey-fill task. Prefers the task's
 * own filledCount; falls back to summing the FHIR and Zori source counts for
 * payloads that only carry the split. Undefined when neither is usable.
 */
export const surveyFillTotalFilledCount = (
  agentOutput?: SurveyFillTask['agentOutput'],
): number | undefined => {
  if (!agentOutput) {
    return undefined;
  }
  const filledCount =
    asNonNegativeCount(agentOutput.filledCount) ??
    asNonNegativeCount(agentOutput.filled_count);
  if (filledCount !== undefined) {
    return filledCount;
  }
  const sourceCounts = surveyFillSourceCounts(agentOutput);
  if (!sourceCounts) {
    return undefined;
  }
  return sourceCounts.fhirFilledCount + sourceCounts.zoriFilledCount;
};

export type SurveyFillLastFilledCaption =
  | { kind: 'countUnavailable'; when: string }
  | {
      kind: 'withCount';
      when: string;
      filledCount: number;
    };

/**
 * Pick last-filled caption values. Successful fills always get a parenthetical:
 * a known total (including 0) or an explicit unavailable fallback.
 */
export const surveyFillLastFilledCaption = (
  when: string,
  filledCount?: number,
): SurveyFillLastFilledCaption => {
  if (filledCount === undefined) {
    return { kind: 'countUnavailable', when };
  }
  return { kind: 'withCount', when, filledCount };
};

/** English default caption text (matches FillFromChartChrome i18n defaultMessage). */
export const formatSurveyFillLastFilledCaptionText = (
  caption: SurveyFillLastFilledCaption,
): string => {
  if (caption.kind === 'countUnavailable') {
    return `Last filled ${caption.when} (pre-filled count unavailable)`;
  }
  if (caption.filledCount === 0) {
    return `Last filled ${caption.when} (no answers pre-filled).`;
  }
  if (caption.filledCount === 1) {
    return `Last filled ${caption.when} (1 answer pre-filled).`;
  }
  return `Last filled ${caption.when} (${caption.filledCount} answers pre-filled).`;
};

export const latestSurveyFillFailureReason = (
  tasks: SurveyFillTask[],
): string | undefined => {
  const newestFailed = [...tasks]
    .filter(task => task.status === 'failed' || task.status === 'error')
    .sort((a, b) => (b.created ?? 0) - (a.created ?? 0))[0];
  const raw =
    newestFailed?.agentOutput?.failureReason ??
    newestFailed?.agentOutput?.failure_reason;
  const trimmed = raw?.trim();
  return trimmed || undefined;
};

export const SURVEY_FILL_IN_FLIGHT = new Set([
  'pending',
  'processing',
  'scheduled',
  'waiting',
]);

export const isSurveyFillKillSwitchedOff = (
  features: Record<string, boolean>,
): boolean => features.surveyFillFromChart === false;

export const resolveSurveyFillSubjectId = (
  explicitSubjectId?: string | null,
  questionnaireResponse?: { subject?: { reference?: string } } | null,
): string | undefined => {
  const trimmed = explicitSubjectId?.trim();
  if (trimmed) {
    return trimmed;
  }
  const reference = questionnaireResponse?.subject?.reference?.trim();
  if (!reference) {
    return undefined;
  }
  return reference.replace(/^Patient\//, '') || undefined;
};

/**
 * survey-service create/GET responses include meta.versionId. Client-stamped
 * draft UUIDs do not — do not invoke Fill until the QR exists on the server.
 */
export const isSurveyFillResponsePersisted = (
  versionId?: string | null,
): boolean => Boolean(versionId?.trim());

export const surveyFillResponseVersionId = (
  questionnaireResponse?: { meta?: { versionId?: string } } | null,
): string | undefined => {
  const versionId = questionnaireResponse?.meta?.versionId?.trim();
  return versionId || undefined;
};

export const isSurveyFillInvokeReady = (
  subjectId?: string,
  projectId?: string,
  responseId?: string,
  versionId?: string | null,
  isServerConfirmed?: boolean,
): boolean =>
  Boolean(
    resolveSurveyFillSubjectId(subjectId) &&
      projectId?.trim() &&
      responseId?.trim() &&
      (isSurveyFillResponsePersisted(versionId) || isServerConfirmed),
  );

/**
 * Everything except the server copy is in place. Fill can be queued and run as
 * soon as the draft persists; a missing subject or project never resolves on
 * its own and stays a hard not-ready.
 */
export const isSurveyFillAwaitingPersist = (
  subjectId?: string,
  projectId?: string,
  responseId?: string,
  versionId?: string | null,
  isServerConfirmed?: boolean,
): boolean =>
  Boolean(
    resolveSurveyFillSubjectId(subjectId) &&
      projectId?.trim() &&
      responseId?.trim() &&
      !isSurveyFillResponsePersisted(versionId) &&
      !isServerConfirmed,
  );

/**
 * "Saving survey before Fill can start…" is only for a queued Fill click while
 * the draft is still unconfirmed. A missing versionId after save (hosts often
 * drop meta) must not keep that caption over last-filled / idle / running.
 */
export const isSurveyFillAwaitingPersistCaption = (
  awaitingPersist?: boolean,
  isInvokeQueued?: boolean,
): boolean => Boolean(awaitingPersist && isInvokeQueued);

/**
 * Whether to show the "AI can make mistakes — verify before completing"
 * disclaimer. Shown whenever this response has ever been filled from chart,
 * including while a newer fill runs: earlier pre-filled answers are already
 * on the form, so the verification warning must not disappear.
 */
export const isSurveyFillDisclaimerVisible = (
  lastSucceededAt: number | undefined,
): boolean => Boolean(lastSucceededAt);

/** Give autosave / confirmation GET time to land before failing the queue. */
export const SURVEY_FILL_PERSIST_WAIT_MS = 30_000;

export type SurveyFillInvokeErrorCode =
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'not-ready'
  | 'unknown';

export type SurveyFillCaptionKind =
  | 'filling'
  | 'invoke-error'
  | 'status-unavailable'
  | 'awaiting-persist'
  | 'last-failed'
  | 'last-filled'
  | 'never';

export const isSurveyFillBusy = ({
  isFilling,
  isRequesting,
  pendingTaskId,
}: {
  isFilling: boolean;
  isRequesting: boolean;
  pendingTaskId?: string;
}): boolean => isFilling || isRequesting || Boolean(pendingTaskId);

export const shouldPollSurveyFill = ({
  showChrome,
  isFilling,
  isRequesting,
  pendingTaskId,
}: {
  showChrome: boolean;
  isFilling: boolean;
  isRequesting: boolean;
  pendingTaskId?: string;
}): boolean =>
  showChrome && isSurveyFillBusy({ isFilling, isRequesting, pendingTaskId });

export const surveyFillErrorFromUnknown = (
  error: unknown,
): SurveyFillInvokeErrorCode => {
  const status =
    error instanceof AxiosError ? error.response?.status : undefined;
  if (status === 403) {
    return 'forbidden';
  }
  if (status === 404) {
    return 'not-found';
  }
  if (status === 409) {
    return 'conflict';
  }
  return 'unknown';
};

export const includeSurveyFillTask = (
  task: SurveyFillTask,
  responseId?: string,
  pendingTaskId?: string,
): boolean =>
  Boolean(pendingTaskId && task.id === pendingTaskId) ||
  !responseId ||
  task.agentInput?.response_id === responseId;

export const surveyFillCaptionKind = ({
  fillRunning,
  invokeError,
  statusError,
  awaitingPersist,
  lastFailedAt,
  lastSucceededAt,
}: {
  fillRunning: boolean;
  invokeError?: SurveyFillInvokeErrorCode;
  statusError?: SurveyFillInvokeErrorCode;
  /** Pass {@link isSurveyFillAwaitingPersistCaption}, not raw awaitingPersist. */
  awaitingPersist?: boolean;
  lastFailedAt?: number;
  lastSucceededAt?: number;
}): SurveyFillCaptionKind => {
  if (fillRunning) {
    return 'filling';
  }
  if (invokeError) {
    return 'invoke-error';
  }
  if (statusError) {
    return 'status-unavailable';
  }
  if (awaitingPersist) {
    return 'awaiting-persist';
  }
  if (lastFailedAt) {
    return 'last-failed';
  }
  if (lastSucceededAt) {
    return 'last-filled';
  }
  return 'never';
};

export const trackedTaskOutcome = (
  tasks: SurveyFillTask[],
  pendingTaskId?: string,
): 'completed' | 'failed' | 'in-flight' | 'unknown' => {
  if (!pendingTaskId) {
    return 'unknown';
  }
  const task = tasks.find(candidate => candidate.id === pendingTaskId);
  if (!task || SURVEY_FILL_IN_FLIGHT.has(task.status)) {
    return 'in-flight';
  }
  return task.status === 'completed' ? 'completed' : 'failed';
};

const surveyService = new SurveyService();

export const useSurveyFill = (options?: {
  onFilledResponse?: (response: fhir.QuestionnaireResponse) => void;
}): {
  showChrome: boolean;
  isInvokeReady: boolean;
  awaitingPersist: boolean;
  isInvokeQueued: boolean;
  isFilling: boolean;
  lastSucceededAt: number | undefined;
  lastFailedAt: number | undefined;
  lastFailureReason: string | undefined;
  filledCount: number | undefined;
  fhirFilledCount: number | undefined;
  zoriFilledCount: number | undefined;
  sourceCounts: SurveyFillSourceCounts | undefined;
  invoke: () => void;
  isRequesting: boolean;
  isBusy: boolean;
  invokeError: SurveyFillInvokeErrorCode | undefined;
  statusUnavailable: boolean;
  statusError: SurveyFillInvokeErrorCode | undefined;
} => {
  const survey = useContext(SurveyContext);
  const surveyResponse = useContext(SurveyResponseContext);
  const contextSubjectId = useContext(SubjectIdContext);
  const subjectId = resolveSurveyFillSubjectId(
    contextSubjectId,
    surveyResponse?.responseWithoutItem,
  );
  const isReadOnly = useContext(IsReadOnlyContext);
  const autoSave = useContext(AutoSaveContext);
  const projectId = useActiveProjectId();
  const features = useSelector(featuresSelectors.selectFeatures);
  const [getResponse] = useService(surveyService.getResponse);
  const onFilledResponse = options?.onFilledResponse;
  const onFilledResponseRef = useRef(onFilledResponse);
  onFilledResponseRef.current = onFilledResponse;
  const responseId = surveyResponse?.id;
  const versionId = surveyFillResponseVersionId(
    surveyResponse?.responseWithoutItem,
  );
  const optedIn = !!survey?.isFillFromChartEnabled();
  const locked = isReadOnly || !!surveyResponse?.isComplete;
  const showChrome =
    optedIn &&
    !isSurveyFillKillSwitchedOff(features) &&
    !!responseId &&
    !locked;
  const [confirmedResponseId, setConfirmedResponseId] = useState<
    string | undefined
  >();
  // Hosts merge the created response into their own state, and not every host
  // carries meta.versionId through. A successful GET is direct proof the draft
  // exists server-side, so Fill is never permanently blocked waiting on meta.
  const isServerConfirmed = Boolean(
    responseId && confirmedResponseId === responseId,
  );
  const isInvokeReady = isSurveyFillInvokeReady(
    subjectId,
    projectId,
    responseId,
    versionId,
    isServerConfirmed,
  );
  const awaitingPersist =
    autoSave &&
    showChrome &&
    isSurveyFillAwaitingPersist(
      subjectId,
      projectId,
      responseId,
      versionId,
      isServerConfirmed,
    );

  const {
    data: invocations,
    refetch,
    isError: invocationsFailed,
    error: invocationsError,
  } = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/survey-fill/invocations',
    { subject_id: subjectId ?? '', response_id: responseId },
    { enabled: showChrome && !!subjectId },
  );

  const [pendingTaskId, setPendingTaskId] = useState<string | undefined>();
  const tasks = useMemo((): SurveyFillTask[] => {
    const rows = (invocations ?? []) as SurveyFillTask[];
    return rows.filter(task =>
      includeSurveyFillTask(task, responseId, pendingTaskId),
    );
  }, [invocations, responseId, pendingTaskId]);

  const inFlight = tasks.filter(task => SURVEY_FILL_IN_FLIGHT.has(task.status));
  const isFilling = inFlight.length > 0;
  const inFlightId = inFlight[0]?.id;
  const succeeded = tasks.filter(task => task.status === 'completed');
  const failed = tasks.filter(
    task => task.status === 'failed' || task.status === 'error',
  );
  const lastSucceededAt = succeeded
    .map(task => task.created)
    .filter((value): value is number => typeof value === 'number')
    .sort((a, b) => b - a)[0];
  const lastFailedAt = failed
    .map(task => task.created)
    .filter((value): value is number => typeof value === 'number')
    .sort((a, b) => b - a)[0];
  const latestSuccess = succeeded.sort(
    (a, b) => (b.created ?? 0) - (a.created ?? 0),
  )[0];
  const sourceCounts = surveyFillSourceCounts(latestSuccess?.agentOutput);
  const totalFilledCount = surveyFillTotalFilledCount(
    latestSuccess?.agentOutput,
  );

  const invokeMutation = apiHooks.useAPIMutation(
    'POST /v1/agents-v2/survey-fill/invoke',
  );
  const [isInvoking, setIsInvoking] = useState(false);
  const [isInvokeQueued, setIsInvokeQueued] = useState(false);
  const [invokeError, setInvokeError] = useState<
    SurveyFillInvokeErrorCode | undefined
  >();
  const isRequesting = Boolean(invokeMutation.isLoading) || isInvoking;
  const settlingTaskId = useRef<string | undefined>();
  const statusError = invocationsFailed
    ? surveyFillErrorFromUnknown(invocationsError)
    : undefined;
  const resolvedInvokeError =
    invokeError ??
    (invokeMutation.isError
      ? surveyFillErrorFromUnknown(invokeMutation.error)
      : undefined);
  const isBusy = isSurveyFillBusy({
    isFilling,
    isRequesting,
    pendingTaskId,
  });

  useEffect(() => {
    if (!pendingTaskId && inFlightId) {
      setPendingTaskId(inFlightId);
    }
  }, [pendingTaskId, inFlightId]);

  useEffect(() => {
    if (
      !shouldPollSurveyFill({
        showChrome,
        isFilling,
        isRequesting,
        pendingTaskId,
      })
    ) {
      return;
    }
    const interval = window.setInterval(() => {
      void refetch();
    }, 5000);
    return () => window.clearInterval(interval);
  }, [showChrome, isFilling, isRequesting, pendingTaskId, refetch]);

  useEffect(() => {
    if (invokeError === 'not-ready' && isInvokeReady) {
      setInvokeError(undefined);
    }
  }, [invokeError, isInvokeReady]);

  const runInvoke = useCallback(() => {
    const readySubjectId = resolveSurveyFillSubjectId(subjectId)!;
    const readyProjectId = projectId!;
    const readyResponseId = responseId!;
    setInvokeError(undefined);
    setIsInvoking(true);
    invokeMutation.mutate(
      {
        subject_id: readySubjectId,
        project_id: readyProjectId,
        response_id: readyResponseId,
      },
      {
        onSuccess: data => {
          setIsInvoking(false);
          if (data?.task_id) {
            setPendingTaskId(data.task_id);
          }
          void refetch();
        },
        onError: error => {
          setIsInvoking(false);
          setInvokeError(surveyFillErrorFromUnknown(error));
          void refetch();
        },
      },
    );
  }, [subjectId, projectId, responseId, invokeMutation, refetch]);

  const invoke = useCallback(() => {
    if (isFilling || locked) {
      return;
    }
    if (isInvokeReady) {
      setIsInvokeQueued(false);
      runInvoke();
      return;
    }
    if (awaitingPersist) {
      // The draft is still being created; run this request when it lands.
      setInvokeError(undefined);
      setIsInvokeQueued(true);
      return;
    }
    setInvokeError('not-ready');
  }, [isInvokeReady, awaitingPersist, isFilling, locked, runInvoke]);

  useEffect(() => {
    if (!isInvokeQueued) {
      return;
    }
    if (locked || !showChrome) {
      setIsInvokeQueued(false);
      return;
    }
    if (isFilling || isRequesting || !isInvokeReady) {
      return;
    }
    setIsInvokeQueued(false);
    runInvoke();
  }, [
    isInvokeQueued,
    locked,
    showChrome,
    isFilling,
    isRequesting,
    isInvokeReady,
    runInvoke,
  ]);

  // While a Fill request is waiting on the draft, confirm the response exists
  // server-side so the queued invoke can run even if the host never surfaces
  // meta.versionId.
  useEffect(() => {
    if (!isInvokeQueued || !awaitingPersist || !projectId || !responseId) {
      return;
    }
    let cancelled = false;
    const confirm = () => {
      void getResponse({ projectId, responseId })
        .then(result => {
          if (!cancelled && result?.data?.id === responseId) {
            setConfirmedResponseId(responseId);
          }
        })
        .catch(() => undefined);
    };
    confirm();
    const interval = window.setInterval(confirm, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isInvokeQueued, awaitingPersist, projectId, responseId, getResponse]);

  // Do not leave "Saving survey…" forever if the draft never becomes ready.
  useEffect(() => {
    if (!isInvokeQueued || !awaitingPersist) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setIsInvokeQueued(false);
      setInvokeError('not-ready');
    }, SURVEY_FILL_PERSIST_WAIT_MS);
    return () => window.clearTimeout(timeout);
  }, [isInvokeQueued, awaitingPersist]);

  useEffect(() => {
    const outcome = trackedTaskOutcome(tasks, pendingTaskId);
    if (outcome === 'unknown' || outcome === 'in-flight' || !pendingTaskId) {
      return;
    }
    if (settlingTaskId.current === pendingTaskId) {
      return;
    }
    settlingTaskId.current = pendingTaskId;
    const finishedId = pendingTaskId;
    setPendingTaskId(undefined);
    setInvokeError(undefined);

    if (outcome !== 'completed' || !projectId || !responseId) {
      return;
    }
    void getResponse({ projectId, responseId })
      .then(result => {
        if (result?.data) {
          onFilledResponseRef.current?.(result.data);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (settlingTaskId.current === finishedId) {
          settlingTaskId.current = undefined;
        }
      });
  }, [tasks, pendingTaskId, projectId, responseId, getResponse]);

  return {
    showChrome,
    isInvokeReady,
    awaitingPersist,
    isInvokeQueued,
    isFilling,
    lastSucceededAt,
    lastFailedAt:
      lastFailedAt && (!lastSucceededAt || lastFailedAt > lastSucceededAt)
        ? lastFailedAt
        : undefined,
    lastFailureReason: latestSurveyFillFailureReason(failed),
    filledCount: totalFilledCount,
    fhirFilledCount: sourceCounts?.fhirFilledCount,
    zoriFilledCount: sourceCounts?.zoriFilledCount,
    sourceCounts,
    invoke,
    isRequesting,
    isBusy,
    invokeError: resolvedInvokeError,
    statusUnavailable: Boolean(statusError),
    statusError,
  };
};
